import React from 'react';
import { Helmet } from 'react-helmet';
import {
    SITE_NAME,
    DEFAULT_TITLE,
    DEFAULT_DESCRIPTION,
    DEFAULT_OG_IMAGE,
    TWITTER_HANDLE,
    absoluteUrl,
} from '../../seo/config';

/**
 * Reusable SEO head manager.
 *
 * @param {string}  title        Page title (site name is appended automatically unless it's the default).
 * @param {string}  description  Meta description (~150-160 chars).
 * @param {string}  path         Route path for canonical + OG url (e.g. "/about").
 * @param {string}  image        Absolute OG image URL.
 * @param {string}  type         OG type ("website" | "article").
 * @param {boolean} noindex      Set true to keep a page out of the index (e.g. admin).
 * @param {Array}   schema       Array of JSON-LD objects to embed.
 * @param {object}  article      Optional { publishedTime, modifiedTime, author } for article pages.
 */
const Seo = ({
    title,
    description = DEFAULT_DESCRIPTION,
    path = '/',
    image = DEFAULT_OG_IMAGE,
    type = 'website',
    noindex = false,
    schema = [],
    article,
}) => {
    const fullTitle = title
        ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
        : DEFAULT_TITLE;
    const canonical = absoluteUrl(path);

    return (
        <Helmet>
            <html lang="en" />
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            {noindex
                ? <meta name="robots" content="noindex, nofollow" />
                : <meta name="robots" content="index, follow, max-image-preview:large" />}

            {/* Open Graph */}
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />
            <meta property="og:locale" content="en_IN" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:site" content={TWITTER_HANDLE} />

            {/* Article metadata */}
            {type === 'article' && article?.publishedTime && (
                <meta property="article:published_time" content={article.publishedTime} />
            )}
            {type === 'article' && article?.modifiedTime && (
                <meta property="article:modified_time" content={article.modifiedTime} />
            )}
            {type === 'article' && article?.author && (
                <meta property="article:author" content={article.author} />
            )}

            {/* JSON-LD structured data */}
            {schema.map((obj, i) => (
                <script type="application/ld+json" key={i}>
                    {JSON.stringify(obj)}
                </script>
            ))}
        </Helmet>
    );
};

export default Seo;
